import { useCallback, useEffect, useMemo, useState } from 'react';
import { CatalogueCard } from '../../components/catalogue/CatalogueCard';
import { DemCustomerFilters, type DemStatusFilter } from '../../components/dem/DemCustomerFilters';
import { PurchasingCompanyCertModal } from '../../components/purchasing/PurchasingCompanyCertModal';
import { PurchasingItemCertModal } from '../../components/purchasing/PurchasingItemCertModal';
import {
  CATALOGUE_PAGE_SIZE,
  loadCatalogueItems,
  matchesCatalogueSearch,
  shouldShowCatalogueItem,
  type CatalogueItem,
} from '../../lib/catalogueItems';
import {
  itemProductCertReports,
  listCertificateFiles,
  type CertificateFileRow,
} from '../../lib/certificateLibrary';
import {
  loadCertificateItemCertMetaMap,
  getCertificateItemCertMeta,
  type CertificateItemCertMeta,
} from '../../lib/certificateRequestLookup';
import { useDemMasterDataRealtime } from '../../hooks/useDemMasterDataRealtime';
import { useFormsSettingsRealtime } from '../../hooks/useFormsSettingsRealtime';

function scrollItemsToTop() {
  const body = document.querySelector('.purch-items-page .catalogue-page__body');
  if (body instanceof HTMLElement) {
    body.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  const page = document.querySelector('.page');
  if (page instanceof HTMLElement) {
    page.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

export function PurchasingItemsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enriching, setEnriching] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [blockedFilter, setBlockedFilter] = useState<DemStatusFilter>('all');
  const [items, setItems] = useState<CatalogueItem[]>([]);

  const [library, setLibrary] = useState<CertificateFileRow[]>([]);
  const [itemMeta, setItemMeta] = useState<Map<string, CertificateItemCertMeta>>(new Map());
  const [certsLoading, setCertsLoading] = useState(true);
  const [certsWarn, setCertsWarn] = useState<string | null>(null);

  const [selected, setSelected] = useState<CatalogueItem | null>(null);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);

  const refreshCertData = useCallback(async () => {
    setCertsLoading(true);
    setCertsWarn(null);
    try {
      const [files, metaResult] = await Promise.all([
        listCertificateFiles({ activeOnly: true }),
        loadCertificateItemCertMetaMap().then(
          (meta) => ({ meta, error: null as string | null }),
          (err) => ({
            meta: new Map<string, CertificateItemCertMeta>(),
            error: err instanceof Error ? err.message : 'Failed to load item vendor/material.',
          }),
        ),
      ]);
      setLibrary(files);
      setItemMeta(metaResult.meta);
      setCertsWarn(metaResult.error);
    } catch (err) {
      setLibrary([]);
      setCertsWarn(err instanceof Error ? err.message : 'Failed to load certificate status.');
    } finally {
      setCertsLoading(false);
    }
  }, []);

  const [reloadToken, setReloadToken] = useState(0);

  useDemMasterDataRealtime({
    include: ['items', 'prices', 'standard_sales_lines'],
    onChange: () => setReloadToken((n) => n + 1),
  });

  useFormsSettingsRealtime({
    tables: ['certificate_files'],
    onChange: () => void refreshCertData(),
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEnriching(true);
    setPage(1);

    void loadCatalogueItems({
      onPartial: (partial) => {
        if (cancelled) return;
        setItems(partial);
        setLoading(false);
      },
    })
      .then(({ items: rows }) => {
        if (cancelled) return;
        setItems(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        setItems([]);
        setError(err instanceof Error ? err.message : 'Failed to load items.');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setEnriching(false);
        }
      });

    void refreshCertData();

    return () => {
      cancelled = true;
    };
  }, [refreshCertData, reloadToken]);

  useEffect(() => {
    setPage(1);
  }, [search, blockedFilter]);

  const visible = useMemo(() => {
    const base = items.filter((item) => shouldShowCatalogueItem(item));
    if (blockedFilter === 'unblocked') return base.filter((item) => !item.is_blocked);
    if (blockedFilter === 'blocked') return base.filter((item) => item.is_blocked);
    return base;
  }, [items, blockedFilter]);

  const filtered = useMemo(
    () => visible.filter((item) => matchesCatalogueSearch(item, search)),
    [visible, search],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / CATALOGUE_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    const start = (safePage - 1) * CATALOGUE_PAGE_SIZE;
    return filtered.slice(start, start + CATALOGUE_PAGE_SIZE);
  }, [filtered, safePage]);

  const from = filtered.length === 0 ? 0 : (safePage - 1) * CATALOGUE_PAGE_SIZE + 1;
  const to = Math.min(safePage * CATALOGUE_PAGE_SIZE, filtered.length);

  const leadText = loading
    ? 'Loading active items…'
    : enriching || certsLoading
      ? 'Syncing catalogue…'
      : `${visible.length.toLocaleString()} item${visible.length === 1 ? '' : 's'}`;

  const emptyMessage = search.trim()
    ? 'No items match your search and filters.'
    : 'No active items found.';

  function openItem(item: CatalogueItem) {
    setSelected(item);
    setItemModalOpen(true);
  }

  function reportsFor(item: CatalogueItem) {
    const meta = getCertificateItemCertMeta(itemMeta, item.code);
    return itemProductCertReports(library, meta?.vendor_no, meta?.material);
  }

  return (
    <div className="catalogue-page dem-items-page purch-items-page">
      <div className="catalogue-page__sticky">
        <header className="catalogue-page__hero">
          <div className="catalogue-page__title-row">
            <p className="catalogue-page__eyebrow">Purchasing · Certificates</p>
            <p className="catalogue-page__lead">{leadText}</p>
          </div>

          <div className="catalogue-page__controls purch-items-page__controls">
            <button
              type="button"
              className="btn-secondary btn-sm purch-items-page__company-btn"
              onClick={() => setCompanyModalOpen(true)}
            >
              Company certificates
            </button>

            <label className="catalogue-page__search">
              <span className="sr-only">Search items</span>
              <input
                type="search"
                className="catalogue-page__search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by code, description, Chinese name or IPG"
                aria-label="Search items"
                disabled={loading && items.length === 0}
              />
            </label>
          </div>
        </header>

        <div className="dem-items-page__filters">
          <DemCustomerFilters value={blockedFilter} onChange={setBlockedFilter} />
        </div>

        {error ? <p className="catalogue-page__error">{error}</p> : null}
        {certsWarn ? <p className="catalogue-page__warn">{certsWarn}</p> : null}

        {!loading || items.length > 0 ? (
          <div className="catalogue-page__toolbar">
            <p className="catalogue-page__count">
              {filtered.length === 0
                ? 'No matches'
                : `Showing ${from.toLocaleString()}–${to.toLocaleString()} of ${filtered.length.toLocaleString()}`}
            </p>
          </div>
        ) : null}
      </div>

      <div className="catalogue-page__body">
        {loading && items.length === 0 ? (
          <div className="catalogue-grid catalogue-grid--skeleton" aria-busy>
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="catalogue-card catalogue-card--skeleton" />
            ))}
          </div>
        ) : pageItems.length === 0 && !error ? (
          <p className="catalogue-page__empty">{emptyMessage}</p>
        ) : (
          <div className="catalogue-grid">
            {pageItems.map((item) => (
              <CatalogueCard
                key={item.code}
                item={item}
                hideCommerce
                certReports={reportsFor(item)}
                selected={itemModalOpen && selected?.code === item.code}
                onSelect={openItem}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && pageItems.length > 0 ? (
          <div className="catalogue-page__pager catalogue-page__pager--bottom">
            <button
              type="button"
              className="catalogue-page__pager-btn"
              disabled={safePage <= 1}
              onClick={() => {
                setPage((p) => Math.max(1, p - 1));
                scrollItemsToTop();
              }}
            >
              Prev
            </button>
            <span className="catalogue-page__pager-status">
              Page {safePage} of {totalPages}
            </span>
            <button
              type="button"
              className="catalogue-page__pager-btn"
              disabled={safePage >= totalPages}
              onClick={() => {
                setPage((p) => Math.min(totalPages, p + 1));
                scrollItemsToTop();
              }}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>

      <PurchasingItemCertModal
        item={selected}
        open={itemModalOpen}
        onClose={() => setItemModalOpen(false)}
        onChanged={() => void refreshCertData()}
      />
      <PurchasingCompanyCertModal
        open={companyModalOpen}
        onClose={() => setCompanyModalOpen(false)}
      />
    </div>
  );
}
